import React, { useEffect, useMemo, useRef, useState } from "react";

const PARTICLE_COUNT = 64;

// Telefony / ekrany dotykowe: statyczne tło zamiast 64 cząsteczek + rozmytych orbów (płynność, bateria)
function useLiteMode() {
  const [lite, setLite] = useState(() => {
    try { return window.matchMedia("(max-width: 767px), (pointer: coarse)").matches; } catch { return false; }
  });
  useEffect(() => {
    let mq;
    try { mq = window.matchMedia("(max-width: 767px), (pointer: coarse)"); } catch { return undefined; }
    const h = () => setLite(mq.matches);
    mq.addEventListener("change", h);
    return () => mq.removeEventListener("change", h);
  }, []);
  return lite;
}

export function AmbientBackground() {
  const lite = useLiteMode();
  if (lite) {
    return (
      <div className="pointer-events-none fixed inset-0 z-0" aria-hidden="true" data-testid="ambient-background">
        <div className="absolute inset-0 ambient-wash" />
        <div className="absolute inset-0 ambient-vignette" />
      </div>
    );
  }
  return <AmbientBackgroundFull />;
}

function AmbientBackgroundFull() {
  const parallaxRef = useRef(null);
  const rafRef = useRef(null);

  useEffect(() => {
    const onScroll = () => {
      if (rafRef.current) return;
      rafRef.current = requestAnimationFrame(() => {
        rafRef.current = null;
        const y = window.scrollY;
        if (parallaxRef.current) {
          parallaxRef.current.style.transform = `translateY(${y * -0.06}px)`;
        }
      });
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", onScroll);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, []);

  const particles = useMemo(() => {
    let seed = 99;
    const rnd = () => { seed = (seed * 9301 + 49297) % 233280; return seed / 233280; };
    return Array.from({ length: PARTICLE_COUNT }, (_, i) => ({
      left: rnd() * 100,
      top: rnd() * 100,
      size: 2 + rnd() * 3.5,
      delay: rnd() * 6,
      duration: 3 + rnd() * 5,
      bright: rnd() > 0.55,
    }));
  }, []);

  return (
    <div className="pointer-events-none fixed inset-0 z-0 overflow-hidden" aria-hidden="true" data-testid="ambient-background">
      {/* aurora orbs with scroll parallax */}
      <div ref={parallaxRef} className="absolute inset-0 will-change-transform">
        <div className="orb-drift-1 absolute -left-40 top-[16%] h-[36rem] w-[36rem] rounded-full bg-cyan-400/20 blur-[100px]" />
        <div className="orb-drift-2 absolute right-0 top-[34%] h-[42rem] w-[42rem] rounded-full bg-blue-500/25 blur-[100px]" />
        <div className="orb-drift-3 absolute left-[18%] top-[70%] h-[34rem] w-[34rem] rounded-full bg-teal-300/25 blur-[100px]" />
        <div className="orb-drift-2 absolute -left-32 top-[118%] h-[36rem] w-[36rem] rounded-full bg-sky-400/25 blur-[100px]" />
        <div className="orb-drift-1 absolute right-[6%] top-[160%] h-[40rem] w-[40rem] rounded-full bg-cyan-400/20 blur-[100px]" />
      </div>
      {/* aurora light beams like in the hero sky */}
      <div className="aurora-beam absolute -top-[20%] left-[12%] h-[150%] w-[22rem] blur-2xl" style={{ background: "linear-gradient(180deg, rgba(34,211,238,0.20), rgba(34,211,238,0.04) 55%, transparent)", transform: "rotate(-16deg)" }} />
      <div className="aurora-beam-rev absolute -top-[20%] right-[18%] h-[150%] w-[18rem] blur-2xl" style={{ background: "linear-gradient(180deg, rgba(45,212,191,0.16), rgba(45,212,191,0.03) 55%, transparent)", transform: "rotate(14deg)" }} />
      {/* sky-glow wash echoing the hero banner */}
      <div className="absolute inset-0 ambient-wash" />
      {/* twinkling particles */}
      {particles.map((p, i) => (
        <span
          key={i}
          className="particle-twinkle absolute rounded-full"
          style={{
            left: `${p.left}%`,
            top: `${p.top}%`,
            width: p.size,
            height: p.size,
            background: p.bright ? "#67e8f9" : "#e0f2fe",
            boxShadow: p.bright ? "0 0 10px 3px rgba(103,232,249,0.6)" : "0 0 6px 1px rgba(224,242,254,0.35)",
            animationDelay: `${p.delay}s`,
            animationDuration: `${p.duration}s`,
          }}
        />
      ))}
      {/* vignette for depth */}
      <div className="absolute inset-0 ambient-vignette" />
    </div>
  );
}
